/* PDP — color/size selection, qty, gallery, variant updates.
   Reads product JSON from <script data-product-json>, syncs state
   on option change, and updates the hidden variant input + price + ATC. */
(() => {
  const section = document.querySelector('[data-section="product"]');
  if (!section) return;

  const dataNode = section.querySelector('[data-product-json]');
  if (!dataNode) return;
  let product;
  try { product = JSON.parse(dataNode.textContent); } catch { return; }

  const variantInput = section.querySelector('[data-variant-input]');
  const main = section.querySelector('.product__main-img');
  const colorWrap = section.querySelector('[data-colors]');
  const sizeWrap = section.querySelector('[data-sizes]');
  const colorLabel = section.querySelector('[data-color-name]');
  const priceEl = section.querySelector('[data-price]');
  const compareEl = section.querySelector('[data-compare-price]');
  const savingsEl = section.querySelector('[data-savings]');
  const addBtn = section.querySelector('.product__add');
  const addText = section.querySelector('[data-add-text]');

  const COLOR_NAMES = new Set(['color', 'colour']);
  const SIZE_NAMES = new Set(['size']);
  const colorIdx = product.options.findIndex((n) => COLOR_NAMES.has(n.toLowerCase()));
  const sizeIdx = product.options.findIndex((n) => SIZE_NAMES.has(n.toLowerCase()));

  const selected = product.options.map(() => null);
  const initialId = Number(variantInput?.value);
  const initial = product.variants.find((v) => v.id === initialId) || product.variants[0];
  initial.options.forEach((v, i) => (selected[i] = v));

  function findVariant() {
    return product.variants.find((v) =>
      v.options.every((val, i) => selected[i] == null || val === selected[i])
    );
  }

  const formatMoney = (cents) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    return '$' + (cents / 100).toFixed(2);
  };

  function update() {
    const variant = findVariant();
    if (!variant) {
      if (addBtn) addBtn.disabled = true;
      if (addText) addText.textContent = 'Unavailable';
      return;
    }
    if (variantInput) variantInput.value = variant.id;
    if (priceEl) priceEl.textContent = formatMoney(variant.price);
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (compareEl) compareEl.textContent = formatMoney(variant.compare_at_price);
      if (savingsEl) savingsEl.textContent = 'Save ' + formatMoney(variant.compare_at_price - variant.price);
    } else {
      if (compareEl) compareEl.textContent = '';
      if (savingsEl) savingsEl.textContent = '';
    }
    if (addBtn) addBtn.disabled = !variant.available;
    if (addText) addText.textContent = variant.available ? 'Add to Cart' : 'Sold Out';

    if (main && variant.featured_image?.src) {
      main.style.opacity = '0';
      setTimeout(() => { main.src = variant.featured_image.src; main.style.opacity = '1'; }, 120);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    if (colorIdx >= 0 && colorLabel) colorLabel.textContent = selected[colorIdx];
  }

  colorWrap?.addEventListener('click', (e) => {
    const btn = e.target.closest('.color-btn');
    if (!btn) return;
    e.preventDefault();
    colorWrap.querySelectorAll('.color-btn').forEach((b) =>
      b.setAttribute('aria-pressed', b === btn ? 'true' : 'false')
    );
    selected[colorIdx] = btn.dataset.colorName;
    update();
  });

  sizeWrap?.addEventListener('click', (e) => {
    const btn = e.target.closest('.size-btn');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    sizeWrap.querySelectorAll('.size-btn').forEach((b) =>
      b.setAttribute('aria-pressed', b === btn ? 'true' : 'false')
    );
    selected[sizeIdx] = btn.dataset.size;
    update();
  });

  const qtyInput = section.querySelector('[data-qty-input]');
  const qtyInc = section.querySelector('[data-qty-inc]');
  const qtyDec = section.querySelector('[data-qty-dec]');
  const getQty = () => Math.max(1, parseInt(qtyInput?.value, 10) || 1);
  qtyInc?.addEventListener('click', () => (qtyInput.value = getQty() + 1));
  qtyDec?.addEventListener('click', () => (qtyInput.value = Math.max(1, getQty() - 1)));
  qtyInput?.addEventListener('input', () => {
    const v = parseInt(qtyInput.value, 10);
    if (!v || v < 1) qtyInput.value = '1';
  });

  const thumbs = section.querySelectorAll('.product__thumb');
  thumbs.forEach((t) => {
    t.addEventListener('click', () => {
      thumbs.forEach((x) => x.setAttribute('aria-current', 'false'));
      t.setAttribute('aria-current', 'true');
      const img = t.querySelector('img');
      if (img && main) {
        main.style.opacity = '0';
        setTimeout(() => { main.src = img.src; main.style.opacity = '1'; }, 120);
      }
    });
  });

  // Thumbnail carousel scrolling.
  const thumbsTrack = section.querySelector('[data-thumbs-track]');
  const thumbsPrev = section.querySelector('[data-thumbs-prev]');
  const thumbsNext = section.querySelector('[data-thumbs-next]');
  if (thumbsTrack) {
    const thumbPage = () => {
      const first = thumbsTrack.querySelector('.product__thumb');
      if (!first) return thumbsTrack.clientWidth;
      const gap = parseFloat(getComputedStyle(thumbsTrack).columnGap || '0') || 8;
      return (first.getBoundingClientRect().width + gap) * 4;
    };
    const updateThumbsArrows = () => {
      const max = thumbsTrack.scrollWidth - thumbsTrack.clientWidth;
      const overflow = max > 2;
      if (thumbsPrev) {
        thumbsPrev.disabled = thumbsTrack.scrollLeft <= 2;
        thumbsPrev.style.display = overflow ? '' : 'none';
      }
      if (thumbsNext) {
        thumbsNext.disabled = thumbsTrack.scrollLeft >= max - 2;
        thumbsNext.style.display = overflow ? '' : 'none';
      }
    };
    thumbsPrev?.addEventListener('click', () => thumbsTrack.scrollBy({ left: -thumbPage(), behavior: 'smooth' }));
    thumbsNext?.addEventListener('click', () => thumbsTrack.scrollBy({ left: thumbPage(), behavior: 'smooth' }));
    thumbsTrack.addEventListener('scroll', updateThumbsArrows, { passive: true });
    window.addEventListener('resize', updateThumbsArrows);
    updateThumbsArrows();
  }

  // Multi-item accordion: open the first, click toggles others.
  section.querySelectorAll('.product__details-item').forEach((item, i) => {
    if (i === 0) item.classList.add('product__details-item--open');
    const btn = item.querySelector('.product__details-q');
    btn?.addEventListener('click', () => {
      const open = item.classList.toggle('product__details-item--open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  });

  if (window.TPHRecent && product.handle) window.TPHRecent.push(product.handle);

  // ——— Synthesize urgency / delivery stats deterministically per handle ———
  function hash(s) {
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return ((h >>> 0) / 0xffffffff);
  }
  const r = hash(product.handle);
  const viewersEl = section.querySelector('[data-viewers]');
  const soldEl = section.querySelector('[data-sold]');
  const deliveryEl = document.querySelector('[data-delivery]');
  if (viewersEl) viewersEl.textContent = Math.round(8 + r * 35);
  if (soldEl) soldEl.textContent = Math.round(4 + hash(product.handle + 'sold') * 28);
  if (deliveryEl) {
    const d1 = new Date(Date.now() + 4 * 24 * 3600 * 1000);
    const d2 = new Date(Date.now() + 7 * 24 * 3600 * 1000);
    const opts = { weekday: 'short', month: 'short', day: 'numeric' };
    deliveryEl.textContent =
      d1.toLocaleDateString('en-US', opts) + ' – ' + d2.toLocaleDateString('en-US', opts);
  }

  // ——— Sticky add-to-cart bar: show after scrolling past the main image ———
  const stickyBar = document.querySelector('[data-sticky]');
  const galleryEl = section.querySelector('.product__gallery');
  if (stickyBar && galleryEl) {
    const onScroll = () => {
      const rect = galleryEl.getBoundingClientRect();
      const past = rect.bottom < 100;
      stickyBar.classList.toggle('product__sticky--visible', past);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }
  const stickyPrice = document.querySelector('[data-sticky-price]');
  const stickyAddBtn = document.querySelector('[data-sticky-add]');

  // ——— Ajax add-to-cart (form + sticky bar) ———
  async function addToCart() {
    const fd = new FormData(form);
    const id = Number(fd.get('id'));
    const qty = Math.max(1, Number(fd.get('quantity')) || 1);
    if (!id || !window.TPHCart) return;
    await window.TPHCart.add({ id, quantity: qty });
    document.dispatchEvent(new CustomEvent('cart:open'));
  }

  const form = section.querySelector('#product-form');
  form?.addEventListener('submit', (e) => {
    e.preventDefault();
    if (window.TPHCart) addToCart();
    else form.submit();
  });
  stickyAddBtn?.addEventListener('click', addToCart);

  // Update sticky price when variant changes
  function updateStickyPrice() {
    const variant = findVariant();
    if (variant && stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
  }
  colorWrap?.addEventListener('click', updateStickyPrice);
  sizeWrap?.addEventListener('click', updateStickyPrice);
})();
